import React from "react";
import { NavComponent } from "./Nav";
import { Routes, Route, Navigate } from "react-router-dom";
import { ErrorModal, SpinningLoader } from "common/components";
import { closeError, appState, useAppDispatch, useAppSelector } from "app";
import { QuotePage } from "features/Quote";
import { HowToPage } from "features/Tutorial";
import { LoginPage } from "features/Login";
import { getEnv, redirectToEgerdau } from "common/services";
import { QuoteLookup } from "features/QuoteLookup";
import { useAppInit, useOktaSession } from "hooks";

export const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { error, loading, customer } = useAppSelector(appState);
  // Resolve BFF auth state first: which providers are enabled and whether a QQT cookie session
  // (e.g. inherited via silent SSO from another Gerdau app) already exists.
  const {
    providersLoading,
    providersError,
    providersRetrying,
    refetchProviders,
    oktaEnabled,
    azureEnabled,
    egerdauEnabled,
    defaultIdp,
    sessionLoading,
    sessionChecked,
    isAuthenticated,
    me,
  } = useOktaSession();
  // The legacy eGerdau bootstrap only runs when Okta is not the active provider — otherwise its
  // boot overlay would cover (and never clear off) the Okta login page. When an Okta session is
  // active, useAppInit instead hydrates the app from the BFF principal. `egerdauEnabled` only
  // names the provider in that overlay's message.
  const { isRedirect, appInitComplete } = useAppInit({
    oktaEnabled,
    egerdauEnabled,
    providersLoading,
    isAuthenticated,
    me,
  });
  const isValidEnv = () => getEnv() !== "UNKNOWN";

  // When Okta is enabled the BFF cookie session (resolved via /auth/me) is the source of truth
  // for browser auth — NOT the legacy eGerdau localStorage token. Driving the decision off the
  // session (rather than `isRedirect`) ensures the silent SSO (/auth/okta/start?silent=true)
  // fires on launch even when a stale eGerdau token is still cached in the browser.
  // GET /auth/providers failed — we don't know which providers are enabled, so neither the Okta
  // login surface nor the legacy eGerdau auto-redirect is safe to drive off it. Show the login
  // shell's retry card instead of guessing.
  if (isValidEnv() && providersError) {
    return (
      <div className="App">
        <LoginPage
          providers={{ okta: false, azure: false, egerdau: false }}
          isAuthenticated={false}
          providersError
          providersRetrying={providersRetrying}
          onRetryProviders={refetchProviders}
        />
      </div>
    );
  }

  if (isValidEnv() && oktaEnabled) {
    if (!isAuthenticated) {
      // No active QQT session yet: show the login surface. On launch it performs the silent
      // SSO (redirect to /auth/okta/start?silent=true); if that fails it renders the provider
      // buttons. Once a cookie session exists it shows the current user. The session check and
      // the app's own boot-loading message (e.g. once authenticated) render as an inline
      // spinner inside the card rather than a full-page overlay.
      return (
        <div className="App">
          <LoginPage
            providers={{
              okta: oktaEnabled,
              azure: azureEnabled,
              egerdau: egerdauEnabled,
              defaultIdp,
            }}
            me={me}
            isAuthenticated={isAuthenticated}
            loadingMessage={
              sessionLoading || !sessionChecked
                ? "Checking your session"
                : loading.show
                  ? loading.message
                  : undefined
            }
          />
        </div>
      );
    }
    // Authenticated via a (possibly inherited) QQT cookie session — fall through to the app.
  } else if (isValidEnv() && !providersLoading && !oktaEnabled) {
    if (egerdauEnabled) {
      // Okta off, eGerdau on (legacy default): preserve the existing auto-redirect to eGerdau.
      if (isRedirect) {
        redirectToEgerdau();
      }
    } else {
      // Neither Okta nor eGerdau is enabled — there is no provider to redirect to, so stay on
      // the login surface instead of sending the user into a disabled eGerdau flow.
      return (
        <div className="App">
          <LoginPage
            providers={{
              okta: oktaEnabled,
              azure: azureEnabled,
              egerdau: egerdauEnabled,
              defaultIdp,
            }}
            isAuthenticated={false}
          />
        </div>
      );
    }
  }

  // Show the Reload action only for errors a page reload could actually
  // fix (service/network/unexpected). For file-validation errors the user
  // needs to edit and re-upload — reload would just lose state.
  const showReload = (() => {
    const t = (error.title || "").toLowerCase();
    if (!t) return true;
    if (t.includes("validation")) return false;
    if (t.includes("upload error")) return false;
    if (t.includes("upload failed")) return false;
    if (t.includes("uploaded file error")) return false;
    if (t.includes("ship-to")) return false;
    if (t.includes("too large")) return false;
    return true;
  })();

  return (
    <div className="App">
      <ErrorModal
        title={error.title}
        message={error.message}
        show={error.show}
        closeButton={!error.critical}
        onClose={() => dispatch(closeError())}
        showReload={showReload}
      />
      <SpinningLoader
        message={loading.message}
        messageDetail={loading.messageDetail}
        show={loading.show}
      />

      {isValidEnv() && (
        <>
          <NavComponent />
          {appInitComplete && (
            <Routes>
              <Route path="/" element={<QuotePage />} />
              <Route path="/how-to" element={<HowToPage />} />
              <Route
                path="/lookup"
                element={customer?.usertype === "salesRep" ? <QuoteLookup /> : <></>}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </>
      )}
    </div>
  );
};
